import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { User, LogOut } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Navbar({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);

  // Scroll handler for hiding/showing navbar
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      
      if (currentScrollY > 100) {
        // Hide when scrolling down
        if (currentScrollY > lastScrollY) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
      } else {
        setIsVisible(true);
      }
      
      setLastScrollY(currentScrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [lastScrollY]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
    navigate('/');
  };

  const handleFAQClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // Navigate to home page first, then scroll to FAQ
      navigate('/');
      setTimeout(() => {
        const faqSection = document.getElementById('faq');
        if (faqSection) {
          faqSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // Already on home page, just scroll
      const faqSection = document.getElementById('faq');
      if (faqSection) {
        faqSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';
  const isInvestorPage = location.pathname.startsWith('/investor');
  const isDashboardPage = location.pathname.startsWith('/dashboard');
  const isBrokerPage = location.pathname.startsWith('/broker');
  const isAdminPage = location.pathname.startsWith('/admin');
  const isLoggedInPage = isInvestorPage || isDashboardPage || isBrokerPage || isAdminPage;

  // Hide navbar on investor/dashboard/broker/admin pages (sidebar handles navigation)
  if (isLoggedInPage) {
    return null;
  }

  return (
    <motion.nav 
      className={`fixed top-0 left-0 right-0 z-50 border-b bg-[#0A0A0A] border-white/10 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
        <div className="flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-logo">
            <img 
              src="https://customer-assets.emergentagent.com/job_f2a37a8d-c1d2-4efb-b85f-2d723209d2a9/artifacts/umcfekor_Screenshot%202026-02-09%20at%2012.41.39%E2%80%AFPM.png" 
              alt="AltSlice Logo" 
              className="h-8 w-auto"
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm transition-colors duration-300 ${
                isActive('/') ? 'text-white' : 'text-white/60 hover:text-white'
              }`}
              data-testid="nav-home"
            >
              Home
            </Link>
            <button
              onClick={handleFAQClick}
              className="text-sm transition-colors duration-300 text-white/60 hover:text-white"
              data-testid="nav-faq"
            >
              FAQ
            </button>
            <Link 
              to="/blog" 
              className={`text-sm transition-colors duration-300 ${
                isActive('/blog') ? 'text-white' : 'text-white/60 hover:text-white'
              }`}
              data-testid="nav-blog"
            >
              Blog
            </Link>
          </div>

          <div className="flex items-center space-x-3">
            {user ? (
              <>
                <Button 
                  onClick={() => navigate('/investor/estates')}
                  className="font-medium px-6 rounded-full bg-white text-black hover:bg-gray-200"
                  data-testid="dashboard-button"
                >
                  Dashboard
                </Button>
                <div className="hidden md:flex items-center space-x-3 px-4 py-2 bg-white/5 rounded-full border border-white/10" data-testid="user-info">
                  <User className="w-4 h-4 text-white" />
                  <span className="text-sm text-white">{user.name}</span>
                </div>
                <Button 
                  onClick={handleLogout}
                  variant="ghost" 
                  size="sm"
                  className="text-white/60 hover:text-white hover:bg-white/5"
                  data-testid="logout-button"
                >
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                <Button 
                  onClick={() => navigate('/broker')}
                  variant="ghost"
                  className="text-sm text-white/80 hover:text-white hover:bg-white/10 font-medium px-4 rounded-full border border-white/20"
                  data-testid="partner-portal-button"
                >
                  Partner Portal
                </Button>
                <Button 
                  onClick={() => navigate('/auth')}
                  className="font-medium px-6 rounded-full bg-white text-black hover:bg-gray-200"
                  data-testid="login-button"
                >
                  Sign In
                </Button>
              </>
            )}
          </div>
        </div>
      </div>
    </motion.nav>
  );
}